/**
 * Profile Overview — compact personal cockpit summary (Macro #23).
 *
 * A high-value, non-overloaded snapshot: current badge, progress to next badge,
 * Available Rewards, Pending Funding, owned parcel summary and referral status.
 * All values backend-authoritative. Deep detail lives in the Rewards / My Grid
 * tabs — this is a glance, not a dashboard dump.
 */
import { useEffect, useState } from "react";
import { formatMicrousdc, toBigInt } from "../../lib/money";
import { rewardsApi, type RewardsSelf } from "../../lib/rewards-api";
import { parcelUtilityApi, type SelfUtility } from "../../lib/parcel-utility-api";
import { Bar, BadgePill, Card, StatTile } from "./primitives";
import { useI18n } from "../../i18n";

const fmt = (n: number) => formatMicrousdc(toBigInt(n));

export function CockpitSummary() {
  const { t } = useI18n();
  const [rewards, setRewards] = useState<RewardsSelf | null>(null);
  const [util, setUtil] = useState<SelfUtility | null>(null);

  useEffect(() => {
    const c = new AbortController();
    void (async () => {
      const [r, u] = await Promise.allSettled([rewardsApi.self(c.signal), parcelUtilityApi.self(c.signal)]);
      if (r.status === "fulfilled") setRewards(r.value);
      if (u.status === "fulfilled") setUtil(u.value);
    })();
    return () => c.abort();
  }, []);

  if (!rewards) return null;
  const inf = rewards.influence;
  const ref = util?.referral;

  return (
    <Card title={t("profile.cockpit.title")} testId="cockpit-summary" right={<BadgePill badge={inf.badge} testId="cockpit-badge" />}>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatTile label={t("profile.cockpit.available")} value={fmt(rewards.available_micro)} testId="cockpit-available" tint="text-teal-200" />
        <StatTile label={t("profile.cockpit.pendingFunding")} value={fmt(rewards.pending_funding_micro)} testId="cockpit-pending" tint="text-amber-200" />
        <StatTile label={t("profile.cockpit.ownedParcels")} value={util ? util.summary.parcel_count : "—"} testId="cockpit-parcels" sub={util ? t("profile.cockpit.slots", { n: util.summary.total_area }) : undefined} />
        <StatTile label={t("profile.cockpit.influence")} value={inf.influence_points.toLocaleString()} testId="cockpit-influence" sub={t("profile.cockpit.lifetimePts")} />
      </div>

      <div className="mt-4">
        <div className="mb-1 flex justify-between font-mono text-[10px] uppercase text-white/45">
          <span>{t("profile.cockpit.progressTo", { badge: inf.next_badge ?? t("profile.cockpit.max") })}</span>
          <span data-testid="cockpit-influence-progress">{inf.progress_pct}%</span>
        </div>
        <Bar value={inf.progress_pct} tint="bg-amber-400/70" />
      </div>

      {ref && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-xl border border-white/10 bg-white/[0.02] p-3">
          <div className="font-mono text-[10px] uppercase tracking-wide text-white/45">{t("profile.cockpit.referralCode")}</div>
          <div className="font-mono text-sm text-white/85" data-testid="cockpit-referral-code">{ref.referral_code ?? "—"}</div>
          <div className="ml-auto flex gap-2 text-[10px] text-white/50">
            <span data-testid="cockpit-referral-qualified">{t("profile.cockpit.qualified", { n: ref.qualified_referral_count })}</span>
            <span>·</span>
            <span data-testid="cockpit-referral-pending">{t("profile.cockpit.pending", { n: ref.pending_referral_count })}</span>
          </div>
        </div>
      )}
    </Card>
  );
}
